import { joinAllArgs } from '@bugbug/core/utils/toolbox';

import type {
  CreateOrUpdateStepDto,
  DefaultStepsParamsList,
  GroupedStepsParams,
} from './steps.types';

import type { Group } from '@bugbug/core/types/groups';
import type { Step } from '@bugbug/core/types/steps';
import type { Test, TestRun } from '@bugbug/core/types/tests';

import { baseApi } from '../baseApi/baseApi';
import {
  addStepsToCurrentTestGroup,
  populateGroupUpdateToAllInstances,
  removeStepsFromCurrentTest,
  updateCurrentTestQuery,
} from '../tests/tests.api';
import { TESTS_API_TAGS } from '../tests/tests.constants';

import { STEPS_API_TAGS } from './steps.constants';
import { groupDefaultStepsParams } from './steps.utils';

export const stepsApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    // GET /steps/<stepId>/
    getStepDetails: builder.query<
      Step,
      { id: Step['id']; testRunId?: TestRun['id']; groupId?: Group['id'] }
    >({
      query: ({ id, testRunId, groupId }) => ({
        url: `/steps/${id}/`,
        method: 'GET',
        params: { testRunId, groupId },
      }),
      keepUnusedDataFor: 30,
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: STEPS_API_TAGS.STEP_TAG, id: joinAllArgs(arg.id, arg.testRunId) },
              { type: STEPS_API_TAGS.STEP_TAG, id: arg.id },
            ]
          : [{ type: STEPS_API_TAGS.STEP_TAG, id: 'ELEMENT' }],
    }),

    // POST /steps/
    createStep: builder.mutation<Step, { step: Step; groupId: Group['id']; atIndex: number }>({
      query: ({ step, ...params }) => ({
        url: '/steps/',
        method: 'POST',
        data: {
          ...step,
          ...params,
        },
      }),
      onQueryStarted: async (
        { step, groupId, atIndex },
        { dispatch, queryFulfilled, getState },
      ) => {
        try {
          const { data: createdStep } = await queryFulfilled;
          dispatch(
            stepsApi.util.upsertQueryData(
              'getStepDetails',
              { id: step.id, testRunId: undefined, groupId: undefined },
              createdStep,
            ),
          );

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            const tempStepToRemoveId = draft.groups[groupId].steps[atIndex];
            removeStepsFromCurrentTest(draft, { ids: [tempStepToRemoveId] });
            addStepsToCurrentTestGroup(draft, { groupId, steps: [createdStep], atIndex });
            populateGroupUpdateToAllInstances(draft, { group: draft.groups[groupId] });
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
      invalidatesTags: [TESTS_API_TAGS.TESTS_LIST_TAG],
    }),

    // PUT /steps/<id>/
    updateStep: builder.mutation<
      Step,
      {
        id: Step['id'];
        data: CreateOrUpdateStepDto<Step>;
      }
    >({
      query: ({ id, data }) => ({
        url: `/steps/${id}/`,
        method: 'PUT',
        data,
      }),
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled, getState }) => {
        try {
          const {
            data: { groupId: _, ...updatedStep },
          } = await queryFulfilled;
          updateCurrentTestQuery(getState, dispatch, (draft) => {
            if (draft.steps[id]) {
              Object.assign(draft.steps[id], updatedStep);
            }
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: STEPS_API_TAGS.STEP_TAG, id }],
    }),

    // PATCH /groups/<groupId>/steps/<id?/
    patchStep: builder.mutation<
      Step,
      { id: Step['id']; groupId: Group['id']; data: Partial<Step> }
    >({
      query: ({ id, groupId, data }) => ({
        url: `/groups/${groupId}/steps/${id}/`,
        method: 'PATCH',
        data,
      }),
      onQueryStarted: async ({ id, data }, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled;
          updateCurrentTestQuery(getState, dispatch, (draft) => {
            if (draft.steps[id]) {
              Object.assign(draft.steps[id], data);
            }
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: STEPS_API_TAGS.STEP_TAG, id }],
    }),

    // DELETE /steps/<stepId>/
    deleteStep: builder.mutation<Step, { id: Step['id']; testId: Test['id'] }>({
      query: ({ id, testId }) => ({
        url: `/steps/${id}/`,
        method: 'DELETE',
        params: {
          testId,
        },
      }),
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled, getState }) => {
        const patches = updateCurrentTestQuery(getState, dispatch, (draft) => {
          const step = draft.steps[id];
          if (step) {
            const group = draft.groups[step.groupId];
            const stepIndex = group.steps.indexOf(id);
            if (stepIndex !== -1) {
              group.steps.splice(stepIndex, 1);
            }

            delete draft.steps[id];
            delete draft.elementsScreenshots[id];
          }
        });

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: [TESTS_API_TAGS.TESTS_LIST_TAG],
    }),

    // PATCH /steps/switch-state/
    switchStepsActivationState: builder.mutation<
      Step,
      { testId: Test['id']; ids: Step['id'][]; isActive: boolean }
    >({
      query: ({ ids, testId, isActive }) => ({
        url: `/steps/switch-state/`,
        method: 'PATCH',
        data: { isActive, stepsIds: ids, testId },
      }),
      onQueryStarted: async ({ ids, isActive }, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled;
          updateCurrentTestQuery(getState, dispatch, (draft) => {
            ids.forEach((id) => {
              if (draft.steps[id]) {
                draft.steps[id].isActive = isActive;
              }
            });
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
      invalidatesTags: (result, error, { ids }) =>
        ids.map((id) => ({ type: STEPS_API_TAGS.STEP_TAG, id })),
    }),

    // GET /steps/default-params/
    getStepsDefaultParams: builder.query<GroupedStepsParams, void>({
      query: () => ({
        url: '/steps/default-params/',
        method: 'GET',
      }),
      transformResponse: (response: DefaultStepsParamsList) => groupDefaultStepsParams(response),
    }),
  }),
});

export const {
  useGetStepDetailsQuery,
  useCreateStepMutation,
  usePatchStepMutation,
  useGetStepsDefaultParamsQuery,
  usePrefetch: usePrefetchSteps,
} = stepsApi;
